import type { ReactNode } from 'react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { cn } from '@/common/lib/cn/cn'
import { Portal } from '@/common/ui/Portal/Portal'
import { Button } from '@/common/ui/Form/Button'
import { Fieldset } from '@/common/ui/Form/Fieldset'
import { Icon } from '@/common/ui/Icon/Icon'
import { Select, SelectOption } from '@/common/ui/Form/Select'

const DEFAULT_GAP = 6
const DEFAULT_WIDTH = 350
const DEFAULT_MAX_HEIGHT = 420

export type ListFilterSelectOption = {
    value: string
    label: string
}

export type ListFilterSelectField<K extends string = string> = {
    key: K
    label: string
    placeholder: string
    ariaLabel: string
    options: ListFilterSelectOption[]
}

export type ListFilterProps = {
    /** Подсветка кнопки при активных фильтрах в URL. */
    active?: boolean
    onApply: () => void
    onClear: () => void
    /** Перед открытием панели (синхронизация драфта с URL и т.п.). */
    onBeforeOpen?: () => void
    'aria-label': string
    width?: number
    maxHeight?: number
    gapPx?: number
    selectSize?: 'sm' | 'md' | 'lg' | 'xl'
    clearLabel?: string
    applyLabel?: string
    clearButtonClassName?: string
    panelClassName?: string
    zIndexClassName?: string
    /** Поля Select. */
    filterSelectFields?: ListFilterSelectField<string>[]
    filterValues?: Record<string, string>
    onFilterValueChange?: (key: string, value: string) => void
    /** Произвольное тело панели (если не заданы `filterSelectFields`). */
    children?: ReactNode
    /** Для `portalContainer` у Select внутри кастомных `children`. */
    onPanelElementChange?: (element: HTMLDivElement | null) => void
}

/**
 * Кнопка фильтра и попап с полями: позиционирование, клик снаружи, Escape, Очистить / Применить.
 */
export const ListFilter = ({
    active,
    onApply,
    onClear,
    onBeforeOpen,
    'aria-label': ariaLabel,
    width = DEFAULT_WIDTH,
    maxHeight = DEFAULT_MAX_HEIGHT,
    gapPx = DEFAULT_GAP,
    selectSize = 'lg',
    clearLabel = 'Очистить',
    applyLabel = 'Применить',
    clearButtonClassName,
    zIndexClassName = 'z-[900]',
    filterSelectFields,
    filterValues,
    onFilterValueChange,
    children,
    onPanelElementChange,
}: ListFilterProps) => {
    const [open, setOpen] = useState(false)
    const triggerRef = useRef<HTMLDivElement>(null)
    const [panelElement, setPanelElement] = useState<HTMLDivElement | null>(null)

    const setPanelRef = useCallback(
        (el: HTMLDivElement | null) => {
            setPanelElement(el)
            onPanelElementChange?.(el)
        },
        [onPanelElementChange]
    )

    const [panelStyle, setPanelStyle] = useState<{
        top?: number
        bottom?: number
        left: number
        width: number
        maxHeight: number
    }>({ left: 0, width, maxHeight })

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current
        if (!trigger) return

        const rect = trigger.getBoundingClientRect()
        const viewportHeight = window.innerHeight
        const viewportWidth = window.innerWidth
        const spaceBelow = viewportHeight - rect.bottom - gapPx
        const spaceAbove = rect.top - gapPx
        const showAbove = spaceBelow < maxHeight && spaceAbove > spaceBelow

        let left = rect.right - width
        if (left < gapPx) left = gapPx
        if (left + width > viewportWidth - gapPx) {
            left = Math.max(gapPx, viewportWidth - width - gapPx)
        }

        const resolvedMaxHeight = showAbove
            ? Math.min(maxHeight, Math.max(spaceAbove, 120))
            : Math.min(maxHeight, Math.max(spaceBelow, 120))

        setPanelStyle({
            ...(showAbove ? { bottom: viewportHeight - rect.top + gapPx } : { top: rect.bottom + gapPx }),
            left,
            width,
            maxHeight: resolvedMaxHeight,
        })
    }, [width, maxHeight, gapPx])

    useLayoutEffect(() => {
        if (!open) return
        updatePosition()
    }, [open, updatePosition])

    useEffect(() => {
        if (!open) return

        const handleScrollOrResize = () => updatePosition()
        window.addEventListener('scroll', handleScrollOrResize, true)
        window.addEventListener('resize', handleScrollOrResize)
        return () => {
            window.removeEventListener('scroll', handleScrollOrResize, true)
            window.removeEventListener('resize', handleScrollOrResize)
        }
    }, [open, updatePosition])

    useEffect(() => {
        if (!open) return

        const handlePointerDown = (event: globalThis.MouseEvent) => {
            const target = event.target as Node
            if (triggerRef.current?.contains(target)) return
            if (panelElement?.contains(target)) return
            if ((event.target as HTMLElement).closest('.custom-select-menu')) return
            setOpen(false)
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        document.addEventListener('mousedown', handlePointerDown)
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('mousedown', handlePointerDown)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [open, panelElement])

    const handleTriggerClick = () => {
        if (open) {
            setOpen(false)
            return
        }
        onBeforeOpen?.()
        setOpen(true)
    }

    const handleApply = () => {
        onApply()
        setOpen(false)
    }

    const handleClear = () => {
        onClear()
        setOpen(false)
    }

    const useBuiltInSelects =
        Boolean(filterSelectFields?.length) && filterValues !== undefined && onFilterValueChange !== undefined

    const filterBody = useBuiltInSelects
        ? filterSelectFields!.map(field => (
              <Fieldset key={String(field.key)} label={field.label}>
                  <Select
                      value={filterValues![field.key] ?? ''}
                      onChange={e => onFilterValueChange!(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      size={selectSize}
                      aria-label={field.ariaLabel}
                      portalContainer={panelElement ?? 'body'}
                      className='w-full min-w-0'
                  >
                      {field.options.map(opt => (
                          <SelectOption key={`${String(field.key)}-${opt.value || 'empty'}`} value={opt.value}>
                              {opt.label}
                          </SelectOption>
                      ))}
                  </Select>
              </Fieldset>
          ))
        : children

    return (
        <>
            <div ref={triggerRef} className='inline-flex shrink-0'>
                <Button
                    type='button'
                    variant='ghost'
                    size='lg'
                    emphasis='accent-secondary'
                    active={active}
                    onClick={handleTriggerClick}
                    disabled={false}
                    aria-label={ariaLabel}
                    aria-haspopup='dialog'
                    aria-expanded={open}
                >
                    <Icon name='filter' size='lg' />
                </Button>
            </div>

            {open && (
                <Portal container='body'>
                    <div
                        ref={setPanelRef}
                        role='dialog'
                        aria-label={ariaLabel}
                        className={cn(
                            'fixed flex flex-col overflow-y-auto',
                            zIndexClassName,
                            'overflow-y-auto rounded-[10px] border border-[rgba(255,255,255,0.05)] bg-[#27272e] p-6 shadow-[0_0_16px_0_rgba(0,0,0,0.25)]'
                        )}
                        style={{
                            ...(panelStyle.top !== undefined && { top: panelStyle.top }),
                            ...(panelStyle.bottom !== undefined && { bottom: panelStyle.bottom }),
                            left: panelStyle.left,
                            width: panelStyle.width,
                            maxHeight: panelStyle.maxHeight,
                        }}
                    >
                        <div className={cn('flex w-full min-w-0 flex-col gap-4')}>{filterBody}</div>
                        <div className={cn('mt-[2rem] flex w-full shrink-0 items-center justify-between')}>
                            <Button
                                variant='ghost'
                                emphasis='secondary'
                                type='button'
                                className={cn(clearButtonClassName)}
                                size='md'
                                onClick={handleClear}
                            >
                                {clearLabel}
                            </Button>
                            <Button type='button' emphasis='accent' size='md' onClick={handleApply}>
                                {applyLabel}
                            </Button>
                        </div>
                    </div>
                </Portal>
            )}
        </>
    )
}
